// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { getSystemColor, mergeWithCss, parseBoolean, sharedBadgeStyle } from "@awc/XDL/Utilities";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import {
    BadgeImagePlacementValues,
    BadgeModel,
    BadgeStyleValues,
    BadgeTypeValues,
} from "@insite/client-framework/Types/ApiModels";
import { useRef } from "react";
import { css } from "styled-components";

interface Props {
    productContext: ProductContextModel;
    showTextBadge?: boolean;
    showImageBadge?: boolean;
    notPositioned?: boolean;
}

export const defaultTag: BadgeModel = {
    badgeColorHexCode: "",
    badgeType: BadgeTypeValues.Text,
    badgeStyle: BadgeStyleValues.Rectangle,
    detailWidgetBadgeSize: "",
    displayOnBadgeWidget: true,
    displayOnProductImages: true,
    displayText: "",
    id: "",
    imageAltText: "",
    largeImageBadgePath: "",
    largeImagePlacement: BadgeImagePlacementValues.BottomLeft,
    largeImageTextSize: "",
    name: "",
    tagName: "",
    otherImageBadgePath: "",
    otherImagePlacement: BadgeImagePlacementValues.BottomLeft,
    otherImageTextSize: "",
    sortOrder: -1,
    textColorHexCode: "",
};

export const hasSaleId = "hasSaleId";
export const hasCustomBadge = "hasCustomBadge";

const xUseOverlayBadge = ({ productContext, showTextBadge, showImageBadge, notPositioned = false }: Props) => {
    let showOverlayBadge = false;
    let overlayStyles: any[] = [];
    let xoverlayBadge = null;

    const hasSale = parseBoolean(productContext.product.properties?.hasSale);

    let badges: BadgeModel[] = [];
    if (productContext.product.badges && productContext.product.badges.length > 0) {
        badges = productContext.product.badges.filter(badge => badge.displayOnProductImages);

        if (!showImageBadge && badges.length > 0) {
            badges = badges.filter(badge => badge.badgeType !== BadgeTypeValues.Image);
        }

        if (!showTextBadge && badges.length > 0) {
            badges = badges.filter(badge => badge.badgeType !== BadgeTypeValues.Text);
        }
    }

    if (hasSale) {
        const xhasSale = {
            ...defaultTag,
            badgeColorHexCode: getSystemColor("primary", 1, true).replace("#", ""),
            displayText: translate("Sale"),
            id: hasSaleId,
            textBadgeWrapperCss: {
                ...mergeWithCss(sharedBadgeStyle.badge, {
                    css: css`
                        position: absolute;
                        bottom: 1px;
                        left: 4px;
                        width: 40px;
                        white-space: nowrap;
                    `,
                }),
            },
        };
        badges?.unshift(xhasSale);
    }

    let visibleBadges = badges.filter(badge => badge.displayOnProductImages);

    if (!showTextBadge && visibleBadges.length > 0) {
        visibleBadges = visibleBadges.filter(badge => badge.badgeType !== BadgeTypeValues.Image);
    }

    if (!showImageBadge && visibleBadges.length > 0) {
        visibleBadges = visibleBadges.filter(badge => badge.badgeType !== BadgeTypeValues.Text);
    }

    if (visibleBadges.length > 0) {
        xoverlayBadge = visibleBadges.slice(0, 2);
        showOverlayBadge = true;
        if (!notPositioned) {
            xoverlayBadge?.map(overlayBadge => {
                const width = overlayBadge.badgeType === "Image" ? 25 : 50;

                let overlayBadgeWrapperStyles = {
                    css: css`
                        position: absolute;
                    `,
                };

                let positioning = "";

                // everything with be a few percentages off of the absolute edge
                if (overlayBadge.largeImagePlacement) {
                    switch (overlayBadge.largeImagePlacement) {
                        case BadgeImagePlacementValues.TopLeft:
                            positioning = "left: 2%; top: 2%;";
                            break;
                        case BadgeImagePlacementValues.TopCenter:
                            positioning = `left: ${(100 - width) / 2}%; top: 2%;`;
                            break;
                        case BadgeImagePlacementValues.TopRight:
                            positioning = "right: 2%; top: 2%;";
                            break;
                        case BadgeImagePlacementValues.BottomLeft:
                            positioning = "left: 2%; bottom: 2%;";
                            break;
                        case BadgeImagePlacementValues.BottomCenter:
                            positioning = `left: ${(100 - width) / 2}%; bottom: 2%;`;
                            break;
                        case BadgeImagePlacementValues.BottomRight:
                            positioning = "right: 2%; bottom: 2%;";
                            break;
                        default:
                            positioning = "left: 2%; top: 2%;";
                    }

                    if (overlayBadgeWrapperStyles?.css) {
                        // insert badge positional data
                        overlayBadgeWrapperStyles.css = overlayBadgeWrapperStyles.css.concat(positioning);
                    }
                }
                return overlayStyles.push(overlayBadgeWrapperStyles);
            });
        }
    }

    const badgeAreaWrapperRef: React.MutableRefObject<HTMLDivElement | null> = useRef(null);
    const overlayBadgeWrapperClickHandler = () => {
        if (badgeAreaWrapperRef.current) {
            (badgeAreaWrapperRef.current.firstChild as HTMLLinkElement).click();
        }
    };

    const result = visibleBadges?.slice(0, 2)?.map((badge, index) => ({
        overlayBadge: badge,
        overlayStyles: overlayStyles[index],
    }));

    return {
        showOverlayBadge,
        overlayBadgeWrapperClickHandler,
        badgeAreaWrapperRef,
        result,
    };
};

export default xUseOverlayBadge;
